/**
 * Call IT Dev - Brand Logo Component
 * Uses the official transparent WebP logo.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
const ROOT_LOGO_URL = '/logo-call-it-dev.webp';

interface LogoProps {
  variant?: 'full' | 'icon' | 'wordmark' | 'stacked';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  darkMode?: boolean;
  animated?: boolean;
}

const sizeConfig = {
  xs: { height: 20, iconSize: 20, fontSize: 10, gap: 4 },
  sm: { height: 28, iconSize: 28, fontSize: 13, gap: 6 },
  md: { height: 36, iconSize: 36, fontSize: 16, gap: 8 },
  lg: { height: 44, iconSize: 44, fontSize: 20, gap: 10 },
  xl: { height: 56, iconSize: 56, fontSize: 26, gap: 12 },
  '2xl': { height: 64, iconSize: 64, fontSize: 30, gap: 14 },
};

/**
 * Logo image — transparent WebP, no blend mode needed
 */
const LogoImage = ({
  width,
  height,
  alt = 'Call IT Dev logo',
}: {
  width?: number;
  height?: number;
  alt?: string;
}) => (
  <img
    src={ROOT_LOGO_URL}
    alt={alt}
    width={width}
    height={height}
    loading="eager"
    decoding="async"
    fetchPriority="high"
    className="flex-shrink-0"
    style={{
      width: width ? `${width}px` : 'auto',
      height: height ? `${height}px` : 'auto',
      objectFit: 'contain',
      objectPosition: 'center',
    }}
  />
);

export const Logo = React.forwardRef<HTMLDivElement, LogoProps>(function Logo({ 
  variant = 'full', 
  size = 'md', 
  className,
  darkMode = true,
  animated = false,
}, ref) {
  const config = sizeConfig[size];

  // Icon-only variant — show just the C icon portion (use full logo scaled small)
  if (variant === 'icon') {
    return (
      <div ref={ref} className={cn(animated && 'transition-transform duration-300 hover:scale-105', className)}>
        <LogoImage width={config.iconSize} height={config.iconSize} alt="Call IT Dev icon" />
      </div>
    );
  }

  // Wordmark-only variant
  if (variant === 'wordmark') {
    const textColor = darkMode ? '#FFFFFF' : '#1B2A4A';
    return (
      <div ref={ref} className={cn(animated && 'transition-transform duration-300 hover:scale-105', className)}>
        <span 
          className="font-heading font-extrabold tracking-tight italic"
          style={{ fontSize: config.fontSize, color: textColor }}
        >
          Call IT Dev
        </span>
      </div>
    );
  }

  // Stacked variant — full logo image
  if (variant === 'stacked') {
    return (
      <div ref={ref} className={cn('flex flex-col items-center', animated && 'transition-transform duration-300 hover:scale-105', className)}>
        <LogoImage width={config.iconSize * 2.5} height={config.iconSize * 2} alt="Call IT Dev" />
      </div>
    );
  }

  // Full logo (default) — full logo image
  return (
    <div ref={ref} className={cn('flex items-center', animated && 'transition-transform duration-300 hover:scale-105', className)}>
      <LogoImage width={config.iconSize * 2.8} height={config.iconSize * 1.5} alt="Call IT Dev" />
    </div>
  );
});

/**
 * Minimal text-only logo for constrained spaces
 */
export const LogoMinimal = React.forwardRef<HTMLSpanElement, { 
  className?: string;
  darkMode?: boolean;
}>(function LogoMinimal({ 
  className,
  darkMode = true 
}, ref) {
  const textColor = darkMode ? '#FFFFFF' : '#1B2A4A';
  
  return (
    <span ref={ref} className={cn('flex items-center gap-1.5', className)}>
      <LogoImage width={20} height={20} alt="Call IT Dev icon" />
      <span className="font-heading font-bold text-sm italic" style={{ color: textColor }}>
        CiD
      </span>
    </span>
  );
});

export default Logo;
